import type { Edge, Node } from '@/libs/workflow-node-ui'
import type { CompanyWorkflow, Phase, PhaseConnectionSlot } from '../domain/workflow'

type NodeData = Record<string, unknown>

type PhaseNodeData = {
	kind: 'phase'
	phaseId: string
	name: string
	teamName: string
	onEdit?: (phaseId: string) => void
	onDelete?: (phaseId: string) => void
	nodeData?: NodeData
}

type StepNodeData = {
	kind: 'step'
	stepId: string
	name: string
	description: string | null
	isInbox: boolean
	transitions: Array<{ id: string; label: string; trigger: 'manual' | 'auto' }>
	onEdit?: (stepId: string) => void
	onDelete?: (stepId: string) => void
	nodeData?: NodeData
}

type PhaseEdgeData = {
	kind: 'phase-connection'
	sourcePhaseId: string
	slot: PhaseConnectionSlot
	targetPhaseId: string
}

type WorkflowInfoNodeData = {
	kind: 'workflow-info'
	workflowId: string
	name: string
	description: string | null
}

type CanvasNodeData = PhaseNodeData | StepNodeData | WorkflowInfoNodeData

type CanvasNode = Node<CanvasNodeData>
type CanvasEdge = Edge<PhaseEdgeData | Record<string, unknown>>

type NodeSize = { width: number; height: number }

// Fallback dimensions used in pass-1 before nodes are measured
const DEFAULT_NODE_WIDTH = 160
const DEFAULT_PHASE_WIDTH = 160
const DEFAULT_PHASE_HEIGHT = 120
const DEFAULT_STEP_HEIGHT = 72

const H_GAP = 80
const PHASE_V_GAP = 80

const w = (id: string, sizes: Map<string, NodeSize>, fallback = DEFAULT_NODE_WIDTH) => sizes.get(id)?.width ?? fallback
const h = (id: string, sizes: Map<string, NodeSize>, fallback: number) => sizes.get(id)?.height ?? fallback

const computeRowHeight = (phase: Phase, sizes: Map<string, NodeSize>): number => {
	const steps = phase.teamWorkflow.steps
	const phaseH = h(phase.id, sizes, DEFAULT_PHASE_HEIGHT)
	const maxStepH = steps.reduce((max, step) => Math.max(max, h(`step-${step.id}`, sizes, DEFAULT_STEP_HEIGHT)), 0)
	return Math.max(phaseH, maxStepH)
}

type LayoutResult = {
	nodes: CanvasNode[]
	edges: CanvasEdge[]
}

const STEP = 'step'
const PHASE = 'phase'

const computeWorkflowCanvasLayout = (
	workflow: CompanyWorkflow,
	teamNameMap: Map<string, string>,
	sizes: Map<string, NodeSize> = new Map(),
): LayoutResult => {
	const nodes: CanvasNode[] = []
	const edges: CanvasEdge[] = []

	// Workflow info node sits above all phase rows
	nodes.push({
		id: 'workflow-info',
		type: 'workflowInfoNode',
		position: { x: 0, y: -120 },
		data: {
			kind: 'workflow-info',
			workflowId: workflow.id,
			name: workflow.name,
			description: workflow.description,
		},
	})

	const sortedPhases = [...workflow.phases].sort((a, b) => a.order - b.order)

	const stepNodeIdMap = new Map<string, { id: string; order: number }>()
	for (const phase of sortedPhases) {
		for (const step of phase.teamWorkflow.steps) {
			stepNodeIdMap.set(step.id, { id: `${STEP}-${step.id}`, order: step.order })
		}
	}

	// Also build a map from transitionId -> stepId so we can resolve transition targets
	const transitionStepMap = new Map<string, string>()
	for (const phase of sortedPhases) {
		for (const step of phase.teamWorkflow.steps) {
			for (const t of step.transitions) {
				transitionStepMap.set(t.id, step.id)
			}
		}
	}

	const phaseRowData: Array<{ phase: Phase; rowY: number; rowHeight: number }> = []
	let rowY = 0
	for (const phase of sortedPhases) {
		const rowHeight = computeRowHeight(phase, sizes)
		phaseRowData.push({ phase, rowY, rowHeight })
		rowY += rowHeight + PHASE_V_GAP
	}

	for (const { phase, rowY: phRowY, rowHeight } of phaseRowData) {
		const teamName = teamNameMap.get(phase.teamId) ?? phase.teamId
		const midY = phRowY + rowHeight / 2
		const phaseNodeId = `${phase.id}`
		const steps = [...phase.teamWorkflow.steps].sort((a, b) => a.order - b.order)

		const phaseW = w(phaseNodeId, sizes, DEFAULT_PHASE_WIDTH)
		const phaseH = h(phaseNodeId, sizes, DEFAULT_PHASE_HEIGHT)

		nodes.push({
			id: phaseNodeId,
			type: 'phaseNode',
			position: { x: 0, y: phRowY + (rowHeight - phaseH) / 2 },
			data: { kind: PHASE, phaseId: phase.id, name: phase.name, teamName },
		})

		for (const [slot, targets] of Object.entries(phase.connections ?? {}) as [PhaseConnectionSlot, string[]][]) {
			for (const targetPhaseId of targets) {
				edges.push({
					id: `edge-${PHASE}-${phase.id}-${slot}-${targetPhaseId}`,
					source: phaseNodeId,
					target: targetPhaseId,
					type: 'smoothstep',
					sourceHandle: `${slot}-source-${phaseNodeId}`,
					targetHandle: `top-target-${targetPhaseId}`,
					data: { kind: 'phase-connection', sourcePhaseId: phaseNodeId, slot, targetPhaseId },
				})
			}
		}

		let cursorX = phaseW + H_GAP

		for (const step of steps) {
			const stepNodeId = `${STEP}-${step.id}`
			const stepW = w(stepNodeId, sizes)
			const stepH = h(stepNodeId, sizes, DEFAULT_STEP_HEIGHT)

			nodes.push({
				id: stepNodeId,
				type: 'stepNode',
				position: { x: cursorX, y: midY - stepH / 2 },
				data: {
					kind: STEP,
					stepId: step.id,
					name: step.name,
					description: step.description,
					isInbox: step.isInbox,
					transitions: step.transitions.map(t => ({ id: t.id, label: t.label, trigger: t.trigger })),
				},
			})

			if (step.isInbox) {
				edges.push({
					id: `edge-${PHASE}-inbox-${phase.id}`,
					source: phaseNodeId,
					sourceHandle: `right-source-${phaseNodeId}`,
					target: stepNodeId,
					targetHandle: `node-target-${step.id}`,
					type: 'simplebezier',
				})
			}

			cursorX += stepW + H_GAP
		}

		// Wire transition edges: each transition's source handle -> its target node
		for (const step of steps) {
			for (const t of step.transitions) {
				const sourceHandle = `transition-source-${t.id}`
				const stepNodeId = `${STEP}-${step.id}`

				if (t.target.kind === 'none') continue

				const sourceOrder = stepNodeIdMap.get(step.id)?.order ?? 0

				if (t.target.kind === 'step') {
					const targetNode = stepNodeIdMap.get(t.target.stepId)
					if (targetNode) {
						const isBackEdge = targetNode.order <= sourceOrder
						edges.push({
							id: `edge-transition-${t.id}`,
							source: stepNodeId,
							sourceHandle,
							target: targetNode.id,
							targetHandle: `node-target-${t.target.stepId}`,
							type: isBackEdge ? 'smoothstep' : 'simplebezier',
						})
					}
				} else if (t.target.kind === 'transition') {
					const ownerStepId = transitionStepMap.get(t.target.transitionId)
					const targetNode = ownerStepId ? stepNodeIdMap.get(ownerStepId) : undefined
					if (targetNode) {
						const isBackEdge = targetNode.order <= sourceOrder
						edges.push({
							id: `edge-transition-${t.id}`,
							source: stepNodeId,
							sourceHandle,
							target: targetNode.id,
							targetHandle: `transition-target-${t.target.transitionId}`,
							type: isBackEdge ? 'smoothstep' : 'simplebezier',
						})
					}
				}
				// phase_handoff edges are not drawn on the canvas (handled by phase connections)
			}
		}
	}

	return { nodes, edges }
}

export type { PhaseNodeData, StepNodeData, WorkflowInfoNodeData, CanvasNodeData, PhaseEdgeData, CanvasNode, CanvasEdge, NodeSize }
export { computeWorkflowCanvasLayout }
